import { useEffect, useSyncExternalStore } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { posthog } from "@/shared/lib/posthog";
import type { CalloutId, CalloutPlacement } from "../constants/callouts";

/** A dismissed callout comes back after this long. */
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000;

const dismissKey = (id: CalloutId) => `callout_dismissed:${id}`;

/**
 * Resolved dismissal state per callout id, shared across every mounted
 * instance. A plain per-component useState would let two mounted placements
 * (e.g. the events list and profile tabs, which React Navigation keeps
 * mounted simultaneously) disagree about whether the callout was dismissed.
 * `undefined` means "not yet read from storage".
 */
const dismissedCache = new Map<CalloutId, boolean>();
const pendingReads = new Map<CalloutId, Promise<void>>();
const listeners = new Map<CalloutId, Set<() => void>>();

function notify(id: CalloutId) {
  for (const callback of listeners.get(id) ?? []) callback();
}

function subscribe(id: CalloutId, callback: () => void) {
  let set = listeners.get(id);
  if (!set) {
    set = new Set();
    listeners.set(id, set);
  }
  set.add(callback);
  return () => set.delete(callback);
}

function getSnapshot(id: CalloutId): boolean | undefined {
  return dismissedCache.get(id);
}

/** Reads the dismissal timestamp once per id, however many instances mount. */
function loadDismissed(id: CalloutId) {
  if (dismissedCache.has(id) || pendingReads.has(id)) return;

  const promise = AsyncStorage.getItem(dismissKey(id)).then((raw) => {
    let value = false;
    if (raw) {
      const age = Date.now() - new Date(raw).getTime();
      value = !Number.isNaN(age) && age < DISMISS_TTL_MS;
    }
    dismissedCache.set(id, value);
    pendingReads.delete(id);
    notify(id);
  });

  pendingReads.set(id, promise);
}

function markDismissed(id: CalloutId) {
  dismissedCache.set(id, true);
  notify(id);
  void AsyncStorage.setItem(dismissKey(id), new Date().toISOString());
}

/**
 * Impressions already captured this session, keyed by `<id>:<placement>`.
 * Module-level so that remounting a screen (tab switches, FlashList recycling
 * its header) doesn't inflate `callout_shown` and skew the click-through rate.
 */
const impressions = new Set<string>();

/** Test-only: clears every module-level callout store between test cases. */
export function resetCalloutState() {
  impressions.clear();
  dismissedCache.clear();
  pendingReads.clear();
  listeners.clear();
}

type UseCalloutOptions = {
  /** Extra caller-owned reason to hide, e.g. the user already used the feature. */
  hidden?: boolean;
};

export function useCallout(
  id: CalloutId,
  placement: CalloutPlacement,
  { hidden = false }: UseCalloutOptions = {}
) {
  // React Compiler (app.json experiments.reactCompiler) memoizes these
  // automatically; a manual useCallback here is redundant.
  const dismissed = useSyncExternalStore(
    (callback) => subscribe(id, callback),
    () => getSnapshot(id)
  );

  useEffect(() => {
    loadDismissed(id);
  }, [id]);

  // Deliberately not gated on a PostHog feature flag. `getFeatureFlag` returns
  // `false` for a flag that doesn't exist, so a flag gate silently hides the
  // callout for everyone until someone remembers to create it. Dismissal plus
  // the caller's `hidden` rule are enough, and an OTA update can pull it.
  const isVisible = !hidden && dismissed === false;

  useEffect(() => {
    if (!isVisible) return;

    const key = `${id}:${placement}`;
    if (impressions.has(key)) return;

    impressions.add(key);
    posthog?.capture("callout_shown", { callout_id: id, placement });
  }, [isVisible, id, placement]);

  const dismiss = () => {
    markDismissed(id);
    posthog?.capture("callout_dismissed", { callout_id: id, placement });
  };

  const trackClick = () => {
    posthog?.capture("callout_clicked", { callout_id: id, placement });
  };

  return { isVisible, dismiss, trackClick };
}
